import { Money } from '@classpass/protobuf-definitions/lib/web/core_types/money';
import { PartnerOrder, PartnerOrderItem } from '@classpass/protobuf-definitions/lib/web/models/partners/sku_order';
import {
  markedFulfilledTypeToJSON,
  orderItemCancellationTypeToJSON,
  processingStatusToJSON,
} from '@classpass/protobuf-definitions/lib/web/models/shared/orders/sku_order';

import { transformKeysToCamelCase } from 'helpers/core';
import { toMajorUnits } from 'helpers/currency-symbol';

import { enumToFeString, toFeLifecycleStatus } from '../shared/SkuEnumHelpers';

export { toFeLifecycleStatus };

const toMoneyWire = money => {
  if (money.currency_code !== undefined || money.currency_units !== undefined) {
    return money;
  }
  if (money.currencyCode !== undefined || money.currencyUnits !== undefined) {
    return { currency_code: money.currencyCode, currency_units: money.currencyUnits };
  }
  return Money.fromJSON(money);
};

export const normalizeMoney = money => {
  if (!money) return null;

  const wire = toMoneyWire(money);

  const currencyCode = wire.currency_code ?? wire.currencyCode;
  const currencyUnits = wire.currency_units ?? wire.currencyUnits;

  if (!currencyCode) {
    return null;
  }

  const amount = toMajorUnits(currencyCode, currencyUnits);
  if (amount === null || Number.isNaN(amount)) return null;

  return { amount, currencyCode };
};

const toIsoString = value => {
  if (!value) return null;
  if (value instanceof Date) return value.toISOString();
  return value;
};

const normalizeSkuOrderCore = core => {
  if (!core) return null;

  return {
    orderId: core.orderId ?? null,
    userId: core.userId ?? null,
    venueId: core.venueId ?? null,
    requestedPickupTime: toIsoString(core.requestedPickupTime),
    requestedPickupIsAsap: core.requestedPickupIsAsap ?? false,
    createdOn: toIsoString(core.createdOn),
    updatedOn: toIsoString(core.updatedOn),
    processingStatus: enumToFeString(core.processingStatus, processingStatusToJSON),
    revision: core.revision ?? null,
    userMarkedPickupAt: toIsoString(core.userMarkedPickupAt),
    markedFulfilledAt: toIsoString(core.markedFulfilledAt),
    markedFulfilledType: enumToFeString(core.markedFulfilledType, markedFulfilledTypeToJSON),
  };
};

const normalizePartnerOrderItem = (apiItem, orderPayableAmount) => {
  const isAlreadyParsed = apiItem?.core?.order_item_id !== undefined || apiItem?.core?.orderItemId !== undefined;
  const item = transformKeysToCamelCase(isAlreadyParsed ? apiItem : PartnerOrderItem.fromJSON(apiItem));
  const core = item.core || {};

  return {
    core: {
      orderItemId: core.orderItemId ?? null,
      cancelledAt: toIsoString(core.cancelledAt),
      cancellationType: enumToFeString(core.cancellationType, orderItemCancellationTypeToJSON),
      itemSummary: core.itemSummary
        ? {
            itemId: core.itemSummary.itemId ?? null,
            name: core.itemSummary.name ?? null,
          }
        : null,
    },
    modifierGroups: item.modifierGroups || [],
    basePayableAmount: normalizeMoney(item.basePayableAmount),
    // Cart is single-item for now; API has no per-item payable_amount. Copy order payable
    // onto each item for flyout display. Remove when cart/multi-item orders ship and the API
    // returns item-level payable amounts.
    payableAmount: orderPayableAmount,
  };
};

export const normalizePartnerOrder = (apiOrder, { venueName } = {}) => {
  const order = transformKeysToCamelCase(PartnerOrder.fromJSON(apiOrder));
  const payableAmount = normalizeMoney(order.payableAmountTotal);
  const orderItems = (order.orderItems || []).map(item => normalizePartnerOrderItem(item, payableAmount));

  return {
    core: normalizeSkuOrderCore(order.core),
    orderItems,
    userName: order.userName || null,
    lifecycleStatus: toFeLifecycleStatus(order.lifecycleStatus),
    payableAmount,
    basePayableAmount: normalizeMoney(order.basePayableAmountTotal),
    venueName: venueName || null,
  };
};
